import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Check, X, RefreshCw, Loader2, AlertCircle, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  ItineraryItemForExtraction,
  EXCLUSION_OPTIONS,
  extractInclusionsFromItems,
  extractExclusionsFromItems,
  PROGRAMME_INCLUSION_OPTIONS,
  getDefaultProgrammeInclusions,
  getDefaultProgrammeExclusions,


  formatInclusionsText,
  formatExclusionsText,
  parseInclusionsText,
  parseExclusionsText,
} from "@/lib/inclusionsExclusionsData";

interface InclusionsExclusionsSelectorProps {
  itineraryItems: ItineraryItemForExtraction[];
  inclusionsValue: string;
  exclusionsValue: string;
  onInclusionsChange: (value: string) => void;
  onExclusionsChange: (value: string) => void;
  isLoadingItems?: boolean;
  /**
   * Optional handler that re-fetches itinerary items from the source and
   * resolves with the latest list. When provided, the "Refresh from itinerary"
   * button will await this before re-extracting inclusions.
   */
  onRefreshItems?: () => Promise<ItineraryItemForExtraction[]>;
}

export function InclusionsExclusionsSelector({
  itineraryItems,
  inclusionsValue,
  exclusionsValue,
  onInclusionsChange,
  onExclusionsChange,
  isLoadingItems = false,
  onRefreshItems,
}: InclusionsExclusionsSelectorProps) {
  const [inclusionsManualMode, setInclusionsManualMode] = useState(false);
  const [exclusionsManualMode, setExclusionsManualMode] = useState(false);
  const [selectedInclusions, setSelectedInclusions] = useState<string[]>([]);
  const [selectedExclusions, setSelectedExclusions] = useState<string[]>([]);
  const [customInclusion, setCustomInclusion] = useState("");
  const [customExclusion, setCustomExclusion] = useState("");
  const [showAddInclusion, setShowAddInclusion] = useState(false);
  const [showAddExclusion, setShowAddExclusion] = useState(false);
  const hasInitialised = useRef(false);

  const [editingInclusion, setEditingInclusion] = useState<string | null>(null);
  const [editingInclusionDraft, setEditingInclusionDraft] = useState("");
  const [editingExclusion, setEditingExclusion] = useState<string | null>(null);
  const [editingExclusionDraft, setEditingExclusionDraft] = useState("");
  const [removedInclusions, setRemovedInclusions] = useState<Set<string>>(new Set());
  const [removedExclusions, setRemovedExclusions] = useState<Set<string>>(new Set());

  // Programme-level defaults are used when nothing has been saved yet. Per-item
  // inclusions/exclusions stay on their items and are only merged in when the
  // user explicitly clicks "Pull from itinerary".
  useEffect(() => {
    if (hasInitialised.current) return;

    const inclusionsLines = parseInclusionsText(inclusionsValue || "");
    const exclusionsLines = parseExclusionsText(exclusionsValue || "");
    const inclusionsEmpty = inclusionsLines.length === 0;
    const exclusionsEmpty = exclusionsLines.length === 0;

    if (inclusionsEmpty) {
      const defaults = getDefaultProgrammeInclusions();
      setSelectedInclusions(defaults);
      onInclusionsChange(formatInclusionsText(defaults));
    } else {
      setSelectedInclusions(inclusionsLines);
    }

    if (exclusionsEmpty) {
      const defaults = getDefaultProgrammeExclusions();
      setSelectedExclusions(defaults);
      onExclusionsChange(formatExclusionsText(defaults));
    } else {
      setSelectedExclusions(exclusionsLines);
    }

    hasInitialised.current = true;
  }, [inclusionsValue, exclusionsValue, onInclusionsChange, onExclusionsChange]);

  // Defensive re-sync: if a parent pushes a new non-empty value (e.g. prefilled
  // from a previous quote) after we've already initialised, adopt it so the UI
  // reflects the parent state instead of stale auto-extracted defaults.
  useEffect(() => {
    if (!hasInitialised.current) return;
    if (inclusionsManualMode) return;
    const parsed = parseInclusionsText(inclusionsValue || "");
    if (parsed.length === 0) return;
    const same =
      parsed.length === selectedInclusions.length &&
      parsed.every((v, i) => v === selectedInclusions[i]);
    if (!same) setSelectedInclusions(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inclusionsValue]);

  useEffect(() => {
    if (!hasInitialised.current) return;
    if (exclusionsManualMode) return;
    const parsed = parseExclusionsText(exclusionsValue || "");
    if (parsed.length === 0) return;
    const same =
      parsed.length === selectedExclusions.length &&
      parsed.every((v, i) => v === selectedExclusions[i]);
    if (!same) setSelectedExclusions(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusionsValue]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshFromItinerary = async () => {
    let sourceItems = itineraryItems;
    if (onRefreshItems) {
      try {
        setIsRefreshing(true);
        sourceItems = await onRefreshItems();
      } catch (err: any) {
        toast.error(err?.message || "Couldn't refresh itinerary items.");
        setIsRefreshing(false);
        return;
      } finally {
        setIsRefreshing(false);
      }
    }
    if (sourceItems.length === 0) {
      toast.warning(
        isLoadingItems
          ? "Itinerary is still loading — try again in a moment."
          : "No itinerary items found. Add items in the Itinerary Builder, then refresh."
      );
      return;
    }
    const autoInclusions = extractInclusionsFromItems(sourceItems).filter(
      (i) => !removedInclusions.has(i)
    );
    if (autoInclusions.length === 0) {
      toast.warning("Nothing to pull — all itinerary items are excluded from this quote.");
      return;
    }
    const autoSet = new Set(autoInclusions);
    // Preserve anything the user added that isn't part of the auto-extraction
    const customEntries = selectedInclusions.filter(i => !autoSet.has(i));
    const merged = Array.from(new Set([...autoInclusions, ...customEntries]));
    setSelectedInclusions(merged);
    onInclusionsChange(formatInclusionsText(merged));

    // Pull any per-item exclusions through as well, keeping existing selections.
    const itemLevelExclusions = extractExclusionsFromItems(sourceItems).filter(
      (e) => !removedExclusions.has(e)
    );
    if (itemLevelExclusions.length > 0) {
      const mergedExclusions = Array.from(
        new Set([...selectedExclusions, ...itemLevelExclusions])
      );
      setSelectedExclusions(mergedExclusions);
      onExclusionsChange(formatExclusionsText(mergedExclusions));
    }

    toast.success(
      `Pulled ${autoInclusions.length} inclusion${autoInclusions.length !== 1 ? "s" : ""} from the itinerary.`
    );
  };

  const commitInclusions = (updated: string[]) => {
    setSelectedInclusions(updated);
    onInclusionsChange(formatInclusionsText(updated));
  };

  const commitExclusions = (updated: string[]) => {
    setSelectedExclusions(updated);
    onExclusionsChange(formatExclusionsText(updated));
  };

  const handleEditInclusionSave = (oldText: string) => {
    const newText = editingInclusionDraft.trim();
    if (!newText || newText === oldText) {
      setEditingInclusion(null);
      return;
    }
    const updated = Array.from(
      new Set(selectedInclusions.map((i) => (i === oldText ? newText : i)))
    );
    commitInclusions(updated);
    setRemovedInclusions((prev) => {
      const next = new Set(prev);
      next.add(oldText);
      return next;
    });
    setEditingInclusion(null);
  };

  const handleDeleteInclusion = (text: string) => {
    const updated = selectedInclusions.filter((i) => i !== text);
    commitInclusions(updated);
    setRemovedInclusions((prev) => {
      const next = new Set(prev);
      next.add(text);
      return next;
    });
  };

  const handleEditExclusionSave = (oldText: string) => {
    const newText = editingExclusionDraft.trim();
    if (!newText || newText === oldText) {
      setEditingExclusion(null);
      return;
    }
    const updated = Array.from(
      new Set(selectedExclusions.map((e) => (e === oldText ? newText : e)))
    );
    commitExclusions(updated);
    setRemovedExclusions((prev) => {
      const next = new Set(prev);
      next.add(oldText);
      return next;
    });
    setEditingExclusion(null);
  };

  const handleDeleteExclusion = (text: string) => {
    const updated = selectedExclusions.filter((e) => e !== text);
    commitExclusions(updated);
    setRemovedExclusions((prev) => {
      const next = new Set(prev);
      next.add(text);
      return next;
    });
  };

  const handleInclusionToggle = (inclusion: string, checked: boolean) => {
    let updated: string[];
    if (checked) {
      updated = [...selectedInclusions, inclusion];
    } else {
      updated = selectedInclusions.filter(i => i !== inclusion);
    }
    setSelectedInclusions(updated);
    onInclusionsChange(formatInclusionsText(updated));
  };

  const handleExclusionToggle = (exclusion: string, checked: boolean) => {
    let updated: string[];
    if (checked) {
      updated = [...selectedExclusions, exclusion];
    } else {
      updated = selectedExclusions.filter(e => e !== exclusion);
    }
    setSelectedExclusions(updated);
    onExclusionsChange(formatExclusionsText(updated));
  };

  const handleAddCustomInclusion = () => {
    if (customInclusion.trim()) {
      const updated = [...selectedInclusions, customInclusion.trim()];
      setSelectedInclusions(updated);
      onInclusionsChange(formatInclusionsText(updated));
      setCustomInclusion("");
      setShowAddInclusion(false);
    }
  };

  const handleAddCustomExclusion = () => {
    if (customExclusion.trim()) {
      const updated = [...selectedExclusions, customExclusion.trim()];
      setSelectedExclusions(updated);
      onExclusionsChange(formatExclusionsText(updated));
      setCustomExclusion("");
      setShowAddExclusion(false);
    }
  };

  const handleManualInclusionsChange = (text: string) => {
    onInclusionsChange(text);
    setSelectedInclusions(parseInclusionsText(text));
  };

  const handleManualExclusionsChange = (text: string) => {
    onExclusionsChange(text);
    setSelectedExclusions(parseExclusionsText(text));
  };

  // Render order: user's selected order first, then any unchecked suggestions.
  // Suggestions are the short programme-level lists, not per-item points.
  const programmeInclusions = PROGRAMME_INCLUSION_OPTIONS.map((o) => o.text);
  const selectedInclusionSet = new Set(selectedInclusions);
  const uncheckedInclusionSuggestions = programmeInclusions.filter(
    (i) => !selectedInclusionSet.has(i) && !removedInclusions.has(i)
  );
  const allInclusionOptions = [...selectedInclusions, ...uncheckedInclusionSuggestions];

  const predefinedExclusionTexts = EXCLUSION_OPTIONS.map((o) => o.text);
  const selectedExclusionSet = new Set(selectedExclusions);
  const uncheckedExclusionSuggestions = predefinedExclusionTexts.filter(
    (e) => !selectedExclusionSet.has(e) && !removedExclusions.has(e)
  );

  const allExclusionOptions = [...selectedExclusions, ...uncheckedExclusionSuggestions];

  const moveInclusion = (text: string, direction: -1 | 1) => {
    const idx = selectedInclusions.indexOf(text);
    if (idx === -1) return;
    const target = idx + direction;
    if (target < 0 || target >= selectedInclusions.length) return;
    const updated = [...selectedInclusions];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    commitInclusions(updated);
  };

  const moveExclusion = (text: string, direction: -1 | 1) => {
    const idx = selectedExclusions.indexOf(text);
    if (idx === -1) return;
    const target = idx + direction;
    if (target < 0 || target >= selectedExclusions.length) return;
    const updated = [...selectedExclusions];
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    commitExclusions(updated);
  };

  return (
    <div className="space-y-6">
      {/* Inclusions Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">What's Included</Label>
          <div className="flex items-center gap-1">
            {!inclusionsManualMode && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefreshFromItinerary}
                disabled={isRefreshing}
                title="Optional: also pull every point from the individual programme items"
              >
                {isRefreshing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Pull item points
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setInclusionsManualMode(!inclusionsManualMode)}
            >
              {inclusionsManualMode ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Use Checkboxes
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit Manually
                </>
              )}
            </Button>
          </div>
        </div>

        {inclusionsManualMode ? (
          <Textarea
            value={inclusionsValue}
            onChange={(e) => handleManualInclusionsChange(e.target.value)}
            rows={6}
            placeholder="• Item 1&#10;• Item 2&#10;• Item 3"
            className="font-mono text-sm"
          />
        ) : (
          <div className="space-y-2 border rounded-md p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground pb-1">
              Programme-wide points only — each item's own inclusions already show under that item
              on the quote. Use <strong>Pull item points</strong> if you also want the full item list here.
            </p>
            {allInclusionOptions.map((inclusion) => (
              <div key={inclusion} className="group flex items-center gap-2">
                <Checkbox
                  id={`incl-${inclusion}`}
                  checked={selectedInclusions.includes(inclusion)}
                  onCheckedChange={(checked) => handleInclusionToggle(inclusion, checked as boolean)}
                />
                {editingInclusion === inclusion ? (
                  <>
                    <Input
                      autoFocus
                      value={editingInclusionDraft}
                      onChange={(e) => setEditingInclusionDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleEditInclusionSave(inclusion); }
                        if (e.key === "Escape") { e.preventDefault(); setEditingInclusion(null); }
                      }}
                      className="flex-1 h-8"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleEditInclusionSave(inclusion)}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditingInclusion(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <label htmlFor={`incl-${inclusion}`} className="text-sm cursor-pointer flex-1">
                      {inclusion}
                    </label>
                    {selectedInclusions.includes(inclusion) && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 opacity-60 hover:opacity-100 disabled:opacity-20"
                          onClick={() => moveInclusion(inclusion, -1)}
                          disabled={selectedInclusions.indexOf(inclusion) === 0}
                          title="Move up"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 opacity-60 hover:opacity-100 disabled:opacity-20"
                          onClick={() => moveInclusion(inclusion, 1)}
                          disabled={selectedInclusions.indexOf(inclusion) === selectedInclusions.length - 1}
                          title="Move down"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 opacity-60 hover:opacity-100"
                      onClick={() => { setEditingInclusion(inclusion); setEditingInclusionDraft(inclusion); }}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 opacity-60 hover:opacity-100 hover:text-destructive"
                      onClick={() => handleDeleteInclusion(inclusion)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}
            
            {showAddInclusion ? (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={customInclusion}
                  onChange={(e) => setCustomInclusion(e.target.value)}
                  placeholder="Custom inclusion..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomInclusion())}
                />
                <Button type="button" size="sm" onClick={handleAddCustomInclusion}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddInclusion(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddInclusion(true)}
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Custom
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Exclusions Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">What's Not Included</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExclusionsManualMode(!exclusionsManualMode)}
          >
            {exclusionsManualMode ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Use Checkboxes
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4 mr-1" />
                Edit Manually
              </>
            )}
          </Button>
        </div>

        {exclusionsManualMode ? (
          <Textarea
            value={exclusionsValue}
            onChange={(e) => handleManualExclusionsChange(e.target.value)}
            rows={6}
            placeholder="• Item 1&#10;• Item 2&#10;• Item 3"
            className="font-mono text-sm"
          />
        ) : (
          <div className="space-y-2 border rounded-md p-3 bg-muted/30">
            {allExclusionOptions.map((exclusion) => (
              <div key={exclusion} className="group flex items-center gap-2">
                <Checkbox
                  id={`excl-${exclusion}`}
                  checked={selectedExclusions.includes(exclusion)}
                  onCheckedChange={(checked) => handleExclusionToggle(exclusion, checked as boolean)}
                />
                {editingExclusion === exclusion ? (
                  <>
                    <Input
                      autoFocus
                      value={editingExclusionDraft}
                      onChange={(e) => setEditingExclusionDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleEditExclusionSave(exclusion); }
                        if (e.key === "Escape") { e.preventDefault(); setEditingExclusion(null); }
                      }}
                      className="flex-1 h-8"
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleEditExclusionSave(exclusion)}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditingExclusion(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <label htmlFor={`excl-${exclusion}`} className="text-sm cursor-pointer flex-1">
                      {exclusion}
                    </label>
                    {selectedExclusions.includes(exclusion) && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 opacity-60 hover:opacity-100 disabled:opacity-20"
                          onClick={() => moveExclusion(exclusion, -1)}
                          disabled={selectedExclusions.indexOf(exclusion) === 0}
                          title="Move up"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 opacity-60 hover:opacity-100 disabled:opacity-20"
                          onClick={() => moveExclusion(exclusion, 1)}
                          disabled={selectedExclusions.indexOf(exclusion) === selectedExclusions.length - 1}
                          title="Move down"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 opacity-60 hover:opacity-100"
                      onClick={() => { setEditingExclusion(exclusion); setEditingExclusionDraft(exclusion); }}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 opacity-60 hover:opacity-100 hover:text-destructive"
                      onClick={() => handleDeleteExclusion(exclusion)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}
            
            {showAddExclusion ? (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={customExclusion}
                  onChange={(e) => setCustomExclusion(e.target.value)}
                  placeholder="Custom exclusion..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomExclusion())}
                />
                <Button type="button" size="sm" onClick={handleAddCustomExclusion}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddExclusion(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddExclusion(true)}
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Custom
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
